
















































































































































import { Component, Vue, Prop, PropSync, Watch, InjectReactive } from 'vue-property-decorator';
import { GQL_EmployeeTaxCardEmployeeSave, GQL_PaycheckPeriodSave } from '@/queries/global';
import { IRules, sValidation } from '@/components/UI/Form/ValidationModule';
import {
  validateRequired,
  validateBetween,
  validateType,
  validateFormat,
  allowEmpty,
} from '@/components/UI/Form/Validations';
import dayjs from 'dayjs';
import { SmartQuery } from 'vue-apollo-decorator';
import { GQL_currentUnitPaycheckPeriods } from '@/queries/__generated__/currentUnitPaycheckPeriods';
import { ALL_PAYCHECK_PERIODS } from '@/queries/PaycheckPeriod';
import { taxDeductionMethods } from '@/app/Employee';
import { GQL_employee_employee_employeeTaxCards_file } from '@/queries/__generated__/employee';

const taxFieldValidation: sValidation = ({ formElements }) =>
  (formElements?.taxTable?.dataValue || '') ||
  (formElements?.taxPercentage?.dataValue || '').toString() ||
  (formElements?.taxExemption?.dataValue || false);

@Component
export default class EmployeeTaxCardForm extends Vue {
  @Prop({ type: Object, required: true }) private employeeTaxCard!: GQL_EmployeeTaxCardEmployeeSave;
  @PropSync('valid', { type: Boolean, default: true }) private validFormSync!: boolean;

  private availableYears: number[] = [];

  @SmartQuery<EmployeeTaxCardForm, GQL_currentUnitPaycheckPeriods>({
    query: ALL_PAYCHECK_PERIODS,
    update: data => data.currentUnit.paycheckPeriods as GQL_PaycheckPeriodSave[],
    result(data) {
      // gets all unique years from bookkeepingPeriod
      this.availableYears = [ ...new Set(data.data.currentUnit.paycheckPeriods.map(period => Number(period.bookkeepingPeriod!.slice(0,4)))) ];
    },
  }) private paycheckPeriods?: GQL_PaycheckPeriodSave[];

  private fileName = '';
  private fileUrl = '';
  private showFileUpload = !this.employeeTaxCard.id;
  private taxDeductionMethods = taxDeductionMethods;

  @InjectReactive() private editable!: boolean;

  private rules: IRules = {
    fetchedAt: [
      { validation: validateRequired(), message: i18n => i18n.t('Error.required', { field: this.t_attr('EmployeeTaxCard', 'fetchedAt') }) },
      { validation: validateType('date'), message: i18n => i18n.t('GQL.Type.date') },
    ],
    calendarYear: [
      { validation: validateRequired(), message: i18n => i18n.t('Error.required', { field: this.t_attr('EmployeeTaxCard', 'calendarYear') }) },
    ],
    taxTable: [
      allowEmpty({
        validation: validateFormat(/^\d{4}$/),
        message: i18n => i18n.t('EmployeeTaxCard.taxTalbeFourDigits', { length: 4 }),
      }),
      {
        validation: taxFieldValidation,
        message: i18n => i18n.t('EmployeeTaxCard.taxTableOrPercentageRequired'),
        triggerBy: [ 'taxPercentage', 'taxExemption' ],
      },
    ],
    taxPercentage: [
      {
        validation: validateType('unumber'),
        message: i18n => i18n.t('GQL.Type.unumber'),
      },
      allowEmpty({
        validation: validateBetween(0,100),
        message: i18n => i18n.t('Error.between', {
          field: this.t_attr('EmployeeTaxCard', 'taxPercentage'), min: 0, max: 100,
        }),
      }),
      {
        validation: taxFieldValidation,
        message: i18n => i18n.t('EmployeeTaxCard.taxTableOrPercentageRequired'),
        triggerBy: [ 'taxTable', 'taxExemption' ],
      },
    ],
    taxExemption: [
      {
        validation: taxFieldValidation,
        message: i18n => i18n.t('EmployeeTaxCard.taxTableOrPercentageRequired'),
        triggerBy: [ 'taxTable', 'taxPercentage' ],
      },
    ],
    taxExemptionAmountLimit: [
      {
        validation: ({formElements, dataValue}) => {
          if (!formElements.taxExemption) return true;
          if (!formElements.taxExemption.dataValue ) return true;

          return dataValue;
        },
        message: i18n => i18n.t('Error.required', { field: this.t_attr('EmployeeTaxCard', 'taxExemptionAmountLimit') }),
        triggerBy: [ 'taxExemption' ],
      },
      {
        validation: ({dataValue}) => {
          if (!dataValue) return true;
          return dataValue.greaterThan(0);
        },
        message: i18n => i18n.t('Paycheck.amountGreaterThanZero'),
      },
    ],
    taxDeductionMethod: [
      { validation: validateRequired(), message: i18n => i18n.t('Error.required', { field: this.t_attr('EmployeeTaxCard', 'taxDeductionMethod') }) },
    ],
  };

  private handleFileUpload(file: File | null): void {
    if (file) this.setFile(file);
    else this.removeFile();
  }

  private setFile(file: File): void {
    this.$set(this.employeeTaxCard, 'file', file);
    this.fileName = file.name;
    this.fileUrl = URL.createObjectURL(file);
    this.showFileUpload = false;
  }

  private removeFile(): void {
    this.$set(this.employeeTaxCard, 'file', null);
    this.fileName = '';
    this.fileUrl = '';
  }

  @Watch('employeeTaxCard.taxExemption')
  private resetTaxExemptionAmountLimit(taxExemption: boolean) {
    if (!taxExemption) {
      this.employeeTaxCard.taxExemptionAmountLimit = null;
    }
  }


  @Watch('employeeTaxCard.fetchedAt')
  private populateCalendarYear(date: string) {
    if (!date) return;

      // From PREV_YEAR-12-01 until YEAR-11-30 it should be YEAR.
      // From YEAR-12-01 it should be YEAR+1.
      // .month() is zero indexed so 11th month is December
      const year = dayjs.strict(date).year();

      if (this.availableYears.includes(year)) {
        if (dayjs.strict(date).month() === 11) {
          this.employeeTaxCard.calendarYear = year + 1;
        } else {
          this.employeeTaxCard.calendarYear = year;
        }
      }
  }

  @Watch('employeeTaxCard.file', { immediate: true })
  private setFileIfReceivedFromGraphQL(): void {
    // If the file was received through GraphQL
    if (this.employeeTaxCard.file && 'signedBlobId' in this.employeeTaxCard.file) {
      const file = (this.employeeTaxCard.file as unknown) as GQL_employee_employee_employeeTaxCards_file;
      if (file.signedBlobId) {
        this.fileName = file.fileName!;
        this.fileUrl = file.url!;
      }
    }
  }
}
